import {
  Box,
  Checkbox,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import type { MenuProps, SelectChangeEvent } from '@mui/material';
import { selectAutoUpdateMilesDescriptionText, vehicleFormHooks } from 'app-components/flux/vehicle/vehicleForm';
import React, { useEffect, useRef, useState } from 'react';
import { makeStyles } from 'tss-react/mui';

import { vehicleSelectors, vehicleThunks } from 'app-components/flux/vehicle';

import { type Account, accountsSelectors } from 'app-components/flux/accounts';
import { businessSelectors } from 'app-components/flux/business';
import { entitlementsSelectors } from 'app-components/flux/entitlements';
import type { KbbVehicleMake, KbbVehicleModel, KbbVehicleTrim } from 'app-components/flux/vehicle/vehicleTypes';
import clsx from 'clsx';
import QAmountField from 'components/QAmountField';
import { useAppDispatch, useAppSelector } from 'hooks/redux';
import { VEHICLE_YEARS } from './utils';
import { deduped } from 'app-components/flux/vehicle/utils';
import StdDialog from 'components/Dialogs/StdDialog';
import StdDialogContent from 'components/Dialogs/StdDialogContent';
import StdDialogActions from 'components/Dialogs/StdDialogActions';
import QButton from 'components/QButton';
import InfoTooltipIcon from 'components/InfoTooltipIcon';
import UsageSelect from 'components/UsageSelect';

const HIDDEN_ICON = () => null;

const useStyles = makeStyles()((theme) => ({
  select: {
    '& .MuiOutlinedInput-notchedOutline': {
      border: 'none',
    },
    '& .MuiInputLabel-outlined.MuiInputLabel-shrink': {
      transform: 'translate(10px, 8px) scale(0.8)',
      padding: 0,
    },
    '& .MuiSelect-select': {
      transform: 'translateY(9px)',
    },
    '& .MuiOutlinedInput-root': {
      background: theme.palette.background.default,
      border: `1px solid ${theme.palette.border.light}`,
      transition: 'border-color 0.15s ease',
      '& .MuiInputBase-input': {
        transform: 'translateY(9px)',
      },
      '&:not(.Mui-disabled):hover, &.Mui-focused': {
        borderColor: theme.palette.primary.main,
      },
      '&.Mui-disabled': {
        borderColor: theme.palette.border.light,
      },
    },
    '& .MuiInputLabel-root': {
      transition: 'color 0.15s ease, transform 0.15s ease',
    },
    '&:has(.MuiOutlinedInput-root:not(.Mui-disabled)):hover .MuiInputLabel-root': {
      color: theme.palette.primary.main,
    },
  },
  selectReadOnly: {
    '& .MuiOutlinedInput-root': {
      background: theme.palette.background.paper,
    },
  },
  inputLabel: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
  },
  inputLabelText: {
    background: theme.palette.background.default,
    padding: theme.spacing(0, 0.75),
  },
  inputLabelTextReadOnly: {
    background: theme.palette.background.paper,
  },
  nameInput: {
    '& input': {
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
  },
  textInput: {
    '& .MuiInputLabel-root': {
      transform: 'translate(20px, 17px) scale(1)',
      transition: 'color 0.15s ease, transform 0.15s ease',
    },
    '& .MuiInputLabel-shrink': {
      transform: 'translate(15px, 8px) scale(0.8)',
    },
  },
  infoTooltipIcon: {
    color: theme.palette.icon.light,
  },
  autoUpdateRadioGroupContainer: {
    width: 'fit-content',
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'flex-start',
    gap: theme.spacing(2),
    margin: 'auto',
  },
  autoUpdateCalculatedLabel: {
    alignItems: 'flex-start',
  },
  autoUpdateDivider: {
    width: '100%',
  },
  autoUpdateToggleDetails: {
    display: 'flex',
    gap: theme.spacing(0.5),
  },
  autoUpdateCustomizeButton: {
    padding: 0,
    '&:hover': {
      backgroundColor: 'transparent',
    },
  },
  usageSelectRenderValue: {
    display: 'flex',
    height: '40px',
    alignItems: 'flex-end',
  },
}));

const LOADING_SPINNER = () => <CircularProgress size={20} sx={{ ml: 'auto', mr: 4 }} />;

const MENU_PROPS: Partial<MenuProps> = {
  slotProps: { paper: { sx: { maxHeight: 400 } } },
};

const toCustomMilesPerMonth = (value: string | number | null | undefined): number | null => {
  if (value === '' || value == null) return null;
  const parsedValue = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(parsedValue) ? parsedValue : null;
};

export const ReadOnlyField = React.memo(function ReadOnlyField({ label, value }: { label: string; value: string }) {
  const { classes } = useStyles();

  return (
    <FormControl fullWidth className={clsx(classes.select, classes.selectReadOnly)}>
      <InputLabel shrink className={classes.inputLabel}>
        <Typography className={clsx(classes.inputLabelText, classes.inputLabelTextReadOnly)}>{label}</Typography>
      </InputLabel>
      <Select
        value={value}
        readOnly
        label={label}
        IconComponent={HIDDEN_ICON}
        renderValue={() => <Typography>{value}</Typography>}
      >
        <MenuItem value={value}>{value}</MenuItem>
      </Select>
      <FormHelperText> </FormHelperText>
    </FormControl>
  );
});

type NameInputProps = { linkTo?: undefined | string; readOnly?: boolean };

export const NameInput = React.memo(function NameInput(props: NameInputProps) {
  const { value: accountName, onValueChange: setAccountName, error, helperText } = vehicleFormHooks.useField('name');
  const { classes } = useStyles();
  const { linkTo, readOnly } = props;

  const [uniqueNameError, setUniqueNameError] = useState<boolean>(false);

  const vehicleAccountsById = useAppSelector(accountsSelectors.getVehicleAccounts);
  const vehicleAccounts = vehicleAccountsById.toList();

  const handleNameChange = (name: string) => {
    setAccountName(name);
    const isDuplicateName = vehicleAccounts.some(
      (account: Account) => account.id !== linkTo && account.name?.toLowerCase() === name?.toLowerCase(),
    );
    setUniqueNameError(isDuplicateName);
  };

  const renderHelperText = uniqueNameError ? 'Account name already exists' : helperText || ' ';

  return (
    <FormControl fullWidth>
      <TextField
        id='vehicle-account-name'
        label='Account Name'
        value={accountName}
        onChange={(e) => {
          const enteredName = e.target.value;
          handleNameChange(enteredName);
        }}
        className={classes.nameInput}
        slotProps={{
          htmlInput: { maxLength: 86, readOnly },
          formHelperText: { error: !!error || uniqueNameError },
          inputLabel: { error: false },
        }}
        autoComplete='off'
        helperText={renderHelperText}
      />
    </FormControl>
  );
});

const BusinessNameItem = () => {
  const { classes } = useStyles();
  const { value: businessId } = vehicleFormHooks.useField('businessId');
  const business = useAppSelector((state) => businessSelectors.getBusinessForId(state, businessId));

  return (
    <Box className={classes.usageSelectRenderValue}>
      <Typography>{business?.name ?? 'Personal'}</Typography>
    </Box>
  );
};

export const BusinessPurposeSelect = React.memo(function BusinessPurposeSelect({ readOnly }: { readOnly?: boolean }) {
  const { value: businessId, onValueChange: setBusinessId } = vehicleFormHooks.useField('businessId');

  const businessEnabled: boolean = useAppSelector(entitlementsSelectors.getBusinessEnabled);
  const showBusinessSelect = useAppSelector(
    (state) => businessSelectors.getBusinessList(state)?.size > 0 && businessEnabled,
  );

  if (!showBusinessSelect) return null;

  return (
    <UsageSelect
      showPersonal
      iconOnly={false}
      disabled={readOnly}
      value={businessId}
      onChange={(_e: React.ChangeEvent<HTMLSelectElement>, newValue: string) => setBusinessId(newValue)}
      slotProps={{
        sx: {
          width: '100%',
          height: '100%',
        },
        SelectProps: {
          renderValue: BusinessNameItem,
        },
        hiddenLabel: false,
        label: 'Account Purpose',
      }}
    />
  );
});

export const EstimateInput = React.memo(function EstimateInput({ readOnly }: { readOnly?: boolean }) {
  const { value: estimate, onValueChange: setEstimate, error, helperText } = vehicleFormHooks.useField('estimate');

  return (
    <FormControl fullWidth>
      <QAmountField
        noNegatives
        editable
        id='vehicle-manual-estimate-input'
        label='Value'
        value={estimate}
        onChange={(e) => {
          const enteredEstimate = e.target.value;
          setEstimate(enteredEstimate);
        }}
        autoComplete='off'
        slotProps={{
          formHelperText: { error: !!error },
          inputLabel: { error: false },
          input: { readOnly },
        }}
        helperText={helperText || ' '}
      />
    </FormControl>
  );
});

export const VinInput = React.memo(function VinInput({ readOnly }: { readOnly?: boolean }) {
  const { value, onValueChange: setVin, error, helperText } = vehicleFormHooks.useField('vin');
  const { classes } = useStyles();

  const tooltipText =
    "Your VIN always has 17 characters and can be found on the driver's doorjamb, driver side dashboard, vehicle registration card, or insurance card.";

  return (
    <FormControl fullWidth>
      <TextField
        id='vehicle-vin-input'
        label='17-digit VIN'
        className={classes.textInput}
        slotProps={{
          formHelperText: { error: !!error },
          inputLabel: { error: false },
          input: {
            readOnly,
            endAdornment: <InfoTooltipIcon className={classes.infoTooltipIcon} tooltipText={tooltipText} />,
          },
        }}
        value={value}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          const enteredVin = e.target.value;
          setVin(enteredVin);
        }}
        autoComplete='off'
        helperText={helperText || ' '}
      />
    </FormControl>
  );
});

export const YearInput = React.memo(function YearInput({
  readOnly,
  readOnlyValue,
}: {
  readOnly?: boolean;
  readOnlyValue?: string;
}) {
  const { classes } = useStyles();
  const reduxDispatch = useAppDispatch();

  const { value: currentYearValue, onValueChange: setYear, error, helperText } = vehicleFormHooks.useField('year');

  if (readOnly) {
    return <ReadOnlyField label='Year' value={readOnlyValue ?? currentYearValue ?? ''} />;
  }

  return (
    <FormControl fullWidth className={classes.select} error={!!error}>
      <InputLabel id='vehicle-year-label' className={classes.inputLabel} error={false}>
        <Typography className={classes.inputLabelText}>Year</Typography>
      </InputLabel>
      <Select
        id='vehicle-year-input'
        value={currentYearValue ?? ''}
        label='Year'
        labelId='vehicle-year-label'
        MenuProps={MENU_PROPS}
        onChange={(e: SelectChangeEvent<string>) => {
          const selectedYear = e.target.value;
          setYear(selectedYear);
          if (selectedYear) reduxDispatch(vehicleThunks.fetchKbbMakes(selectedYear));
        }}
      >
        {VEHICLE_YEARS().map((year) => (
          <MenuItem key={`vehicle-input-year-${year}`} value={year}>
            {year}
          </MenuItem>
        ))}
      </Select>
      <FormHelperText>{helperText || ' '}</FormHelperText>
    </FormControl>
  );
});

export const MakeInput = React.memo(function MakeInput({
  readOnly,
  readOnlyValue,
}: {
  readOnly?: boolean;
  readOnlyValue?: string;
}) {
  const { classes } = useStyles();
  const reduxDispatch = useAppDispatch();

  const { value: currentMake, onValueChange: setMake, error, helperText } = vehicleFormHooks.useField('make');
  const selectedYear = vehicleFormHooks.useSelector(({ state }) => state.year);
  const selectedMake = vehicleFormHooks.useSelector(({ state }) => state.make);

  const makeOptions = useAppSelector(vehicleSelectors.selectKbbMakeOptions);
  const makeOptionsIncludingSelectedAccount = deduped(makeOptions, (m) => m.makeId, selectedMake);
  const areMakesLoading = useAppSelector(vehicleSelectors.getKbbMakesLoading);

  const isDisabled = areMakesLoading || !selectedYear || makeOptionsIncludingSelectedAccount.length === 0;
  const hasFetchedOnOpenRef = useRef(false);

  const selectedMakeRef = useRef<HTMLLIElement>(null);
  useEffect(() => {
    if (selectedMakeRef.current) {
      selectedMakeRef.current.scrollIntoView({ block: 'nearest' });
    }
  }, [makeOptions.length]);

  if (readOnly) {
    return <ReadOnlyField label='Make' value={readOnlyValue ?? currentMake?.makeName ?? ''} />;
  }

  return (
    <FormControl fullWidth className={classes.select} error={!!error} disabled={isDisabled}>
      <InputLabel id='vehicle-make-label' className={classes.inputLabel} error={false}>
        <Typography className={classes.inputLabelText}>Make</Typography>
        {areMakesLoading && <LOADING_SPINNER />}
      </InputLabel>
      <Select
        id='vehicle-make-input'
        value={currentMake?.makeId ?? ''}
        label='Make'
        labelId='vehicle-make-label'
        MenuProps={MENU_PROPS}
        onOpen={() => {
          if (
            !hasFetchedOnOpenRef.current &&
            !isDisabled &&
            selectedYear &&
            makeOptions.length === 0 &&
            !areMakesLoading
          ) {
            hasFetchedOnOpenRef.current = true;
            reduxDispatch(vehicleThunks.fetchKbbMakes(selectedYear));
          }
        }}
        onChange={(e: SelectChangeEvent<number>) => {
          const selectedMakeId = Number(e.target.value);
          const selectedMake = makeOptionsIncludingSelectedAccount?.find((make) => make.makeId === selectedMakeId);

          if (selectedMake) {
            setMake({ makeId: selectedMake.makeId, makeName: selectedMake.makeName });
          }
          if (selectedYear && selectedMake) {
            reduxDispatch(vehicleThunks.fetchKbbModels({ year: selectedYear, makeId: selectedMake.makeId }));
          }
        }}
      >
        {makeOptionsIncludingSelectedAccount?.map((make: KbbVehicleMake) => (
          <MenuItem
            key={`vehicle-input-${make.makeId}`}
            value={make.makeId}
            ref={make.makeId === currentMake?.makeId ? selectedMakeRef : undefined}
          >
            {make.makeName}
          </MenuItem>
        ))}
      </Select>
      <FormHelperText>{helperText || ' '}</FormHelperText>
    </FormControl>
  );
});

export const ModelInput = React.memo(function ModelInput({
  readOnly,
  readOnlyValue,
}: {
  readOnly?: boolean;
  readOnlyValue?: string;
}) {
  const { classes } = useStyles();
  const reduxDispatch = useAppDispatch();

  const { value: currentModel, onValueChange: setModel, error, helperText } = vehicleFormHooks.useField('model');
  const selectedYear = vehicleFormHooks.useSelector(({ state }) => state.year);
  const selectedMake = vehicleFormHooks.useSelector(({ state }) => state.make);

  const modelOptions = useAppSelector(vehicleSelectors.selectKbbModelOptions);
  const modelOptionsIncludingSelected = deduped(modelOptions, (m) => m.modelId, currentModel);
  const areModelsLoading = useAppSelector(vehicleSelectors.getKbbModelsLoading);

  const isDisabled = areModelsLoading || !selectedYear || !selectedMake || modelOptionsIncludingSelected.length === 0;
  const hasFetchedOnOpenRef = useRef(false);

  const selectedModelRef = useRef<HTMLLIElement>(null);
  useEffect(() => {
    if (selectedModelRef.current) {
      selectedModelRef.current.scrollIntoView({ block: 'nearest' });
    }
  }, [modelOptions.length]);

  if (readOnly) {
    return <ReadOnlyField label='Model' value={readOnlyValue ?? currentModel?.modelName ?? ''} />;
  }

  return (
    <FormControl fullWidth className={classes.select} error={!!error} disabled={isDisabled}>
      <InputLabel id='vehicle-model-label' className={classes.inputLabel} error={false}>
        <Typography className={classes.inputLabelText}>Model</Typography>
        {areModelsLoading && <LOADING_SPINNER />}
      </InputLabel>
      <Select
        id='vehicle-model-input'
        value={currentModel?.modelId ?? ''}
        label='Model'
        labelId='vehicle-model-label'
        MenuProps={MENU_PROPS}
        onOpen={() => {
          if (
            !hasFetchedOnOpenRef.current &&
            !isDisabled &&
            selectedYear &&
            selectedMake &&
            modelOptions.length === 0 &&
            !areModelsLoading
          ) {
            hasFetchedOnOpenRef.current = true;
            reduxDispatch(vehicleThunks.fetchKbbModels({ year: selectedYear, makeId: selectedMake.makeId }));
          }
        }}
        onChange={(e: SelectChangeEvent<number>) => {
          const selectedModelId = Number(e.target.value);
          const selectedModel = modelOptionsIncludingSelected?.find(
            (modelOptions) => modelOptions.modelId === selectedModelId,
          );

          if (selectedModel) {
            setModel({ modelId: selectedModel.modelId, modelName: selectedModel.modelName });
          }
          if (selectedYear && selectedMake && selectedModelId) {
            reduxDispatch(
              vehicleThunks.fetchKbbTrims({
                year: selectedYear,
                makeId: selectedMake.makeId,
                modelId: selectedModelId,
              }),
            );
          }
        }}
      >
        {modelOptionsIncludingSelected?.map((model: KbbVehicleModel) => (
          <MenuItem
            key={`vehicle-input-${model.modelId}`}
            value={model.modelId}
            ref={model.modelId === currentModel?.modelId ? selectedModelRef : undefined}
          >
            {model.modelName}
          </MenuItem>
        ))}
      </Select>
      <FormHelperText>{helperText || ' '}</FormHelperText>
    </FormControl>
  );
});

export const TrimInput = React.memo(function TrimInput({
  readOnly,
  readOnlyValue,
}: {
  readOnly?: boolean;
  readOnlyValue?: string;
}) {
  const { classes } = useStyles();
  const reduxDispatch = useAppDispatch();

  const { value: currentTrim, onValueChange: setTrim, error, helperText } = vehicleFormHooks.useField('trim');
  const selectedYear = vehicleFormHooks.useSelector(({ state }) => state.year);
  const selectedMake = vehicleFormHooks.useSelector(({ state }) => state.make);
  const selectedModel = vehicleFormHooks.useSelector(({ state }) => state.model);

  const trimOptions = useAppSelector(vehicleSelectors.selectKbbTrimOptions);
  const trimOptionsIncludingSelected = deduped(trimOptions, (t) => t.trimId, currentTrim);
  const areTrimsLoading = useAppSelector(vehicleSelectors.getKbbTrimsLoading);

  const isDisabled = areTrimsLoading || !selectedYear || trimOptionsIncludingSelected.length === 0;
  const hasFetchedOnOpenRef = useRef(false);

  const selectedTrimRef = useRef<HTMLLIElement>(null);
  useEffect(() => {
    if (selectedTrimRef.current) {
      selectedTrimRef.current.scrollIntoView({ block: 'nearest' });
    }
  }, [trimOptions.length]);

  if (readOnly) {
    return <ReadOnlyField label='Trim' value={readOnlyValue ?? currentTrim?.trimName ?? ''} />;
  }

  return (
    <FormControl fullWidth className={classes.select} error={!!error} disabled={isDisabled}>
      <InputLabel id='vehicle-trim-label' className={classes.inputLabel} error={false}>
        <Typography className={classes.inputLabelText}>Trim</Typography>
        {areTrimsLoading && <LOADING_SPINNER />}
      </InputLabel>
      <Select
        id='vehicle-trim-input'
        value={currentTrim?.trimId ?? ''}
        label='Trim'
        labelId='vehicle-trim-label'
        MenuProps={MENU_PROPS}
        onOpen={() => {
          if (
            !hasFetchedOnOpenRef.current &&
            !isDisabled &&
            selectedYear &&
            selectedMake &&
            selectedModel &&
            trimOptions.length === 0 &&
            !areTrimsLoading
          ) {
            hasFetchedOnOpenRef.current = true;
            reduxDispatch(
              vehicleThunks.fetchKbbTrims({
                year: selectedYear,
                makeId: selectedMake.makeId,
                modelId: selectedModel.modelId,
              }),
            );
          }
        }}
        onChange={(e: SelectChangeEvent<number>) => {
          const selectedTrimId = Number(e.target.value);
          const selectedTrim = trimOptionsIncludingSelected.find((trim) => trim.trimId === selectedTrimId);

          if (selectedTrim) {
            setTrim({
              trimId: selectedTrim.trimId,
              trimName: selectedTrim.trimName,
              modelYearId: selectedTrim.modelYearId,
            });
          }
        }}
      >
        {trimOptionsIncludingSelected?.map((trim: KbbVehicleTrim) => (
          <MenuItem
            key={`vehicle-input-${trim.trimId}`}
            value={trim.trimId}
            ref={trim.trimId === currentTrim?.trimId ? selectedTrimRef : undefined}
          >
            {trim.trimName}
          </MenuItem>
        ))}
      </Select>
      <FormHelperText>{helperText || ' '}</FormHelperText>
    </FormControl>
  );
});

export const MilesInput = React.memo(function MilesInput({
  readOnly,
  readOnlyValue,
}: {
  readOnly?: boolean;
  readOnlyValue?: string;
}) {
  const { value: currentMilesValue, onValueChange: setMiles, error, helperText } = vehicleFormHooks.useField('miles');
  const { classes } = useStyles();

  if (readOnly) {
    return <ReadOnlyField label='Miles' value={readOnlyValue ?? currentMilesValue ?? ''} />;
  }

  return (
    <FormControl fullWidth>
      <QAmountField
        fullWidth
        noNegatives
        hideCurrency
        decimals={0}
        startAdornment={undefined}
        editable
        id='vehicle-miles-input'
        label='Miles'
        className={classes.textInput}
        value={currentMilesValue ?? ''}
        onChange={(e) => {
          const enteredMiles = e.target.value;
          setMiles(enteredMiles);
        }}
        autoComplete='off'
        helperText={helperText || ' '}
        slotProps={{
          formHelperText: { error: !!error },
          inputLabel: { error: false },
        }}
      />
    </FormControl>
  );
});

export const ZipCodeInput = React.memo(function ZipCodeInput({
  readOnly,
  readOnlyValue,
}: {
  readOnly?: boolean;
  readOnlyValue?: string;
}) {
  const { value: currentZipCode, onValueChange: setZipCode, error, helperText } = vehicleFormHooks.useField('zipCode');
  const { classes } = useStyles();

  if (readOnly) {
    return <ReadOnlyField label='ZIP Code' value={readOnlyValue ?? currentZipCode ?? ''} />;
  }

  return (
    <FormControl fullWidth>
      <TextField
        id='vehicle-zipCode-input'
        label='ZIP Code'
        className={classes.textInput}
        value={currentZipCode ?? ''}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          const enteredZipCode = e.target.value;
          setZipCode(enteredZipCode);
        }}
        autoComplete='off'
        helperText={helperText || ' '}
        slotProps={{
          formHelperText: { error: !!error },
          inputLabel: { error: false },
        }}
      />
    </FormControl>
  );
});

type AutoUpdateMilesRadioGroupProps = {
  readOnly?: boolean;
};
export const AutoUpdateMilesRadioGroup = React.memo(function AutoUpdateMilesRadioGroup({
  readOnly,
}: AutoUpdateMilesRadioGroupProps) {
  const { classes } = useStyles();
  const {
    value: autoUpdateMiles,
    onValueChange: setAutoUpdateMiles,
    error,
    helperText,
  } = vehicleFormHooks.useField('autoUpdateMiles');

  const radioValue = autoUpdateMiles?.useCalculatedMilesPerMonth ? 'calculated' : autoUpdateMiles ? 'custom' : 'none';

  const handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    if (value === 'calculated') {
      setAutoUpdateMiles({
        autoUpdateMiles: true,
        useCalculatedMilesPerMonth: true,
        customMilesPerMonth: null,
      });
    } else if (value === 'custom') {
      setAutoUpdateMiles({
        autoUpdateMiles: true,
        useCalculatedMilesPerMonth: false,
        customMilesPerMonth: autoUpdateMiles?.customMilesPerMonth ?? null,
      });
    }
  };

  return (
    <FormControl fullWidth>
      <RadioGroup value={radioValue} onChange={handleRadioChange}>
        <Box className={classes.autoUpdateRadioGroupContainer}>
          <FormControlLabel
            value='calculated'
            control={<Radio inputProps={{ readOnly }} />}
            labelPlacement='end'
            label={
              <Box>
                <Typography>Add monthly average every 30 days</Typography>
                <Typography variant='paragraphMedium' color='textSecondary'>
                  This will be calculated for you
                </Typography>
              </Box>
            }
            className={classes.autoUpdateCalculatedLabel}
          />
          <Divider className={classes.autoUpdateDivider} />
          <FormControlLabel
            value='custom'
            control={<Radio inputProps={{ readOnly }} />}
            label={<Typography>Add custom amount every 30 days</Typography>}
          />
          <QAmountField
            fullWidth
            noNegatives
            hideCurrency
            decimals={0}
            startAdornment={undefined}
            editable
            id='vehicle-custom-increment-input'
            label='Miles Increment'
            value={
              autoUpdateMiles && !autoUpdateMiles.useCalculatedMilesPerMonth
                ? (autoUpdateMiles.customMilesPerMonth ?? 0)
                : 0
            }
            onChange={(e) => {
              const enteredIncrement = toCustomMilesPerMonth(e.target.value);
              if (autoUpdateMiles && !autoUpdateMiles.useCalculatedMilesPerMonth) {
                setAutoUpdateMiles({ ...autoUpdateMiles, customMilesPerMonth: enteredIncrement });
              }
            }}
            autoComplete='off'
            helperText={helperText || ' '}
            slotProps={{
              formHelperText: {
                error: !!error,
              },
              inputLabel: {
                error: false,
              },
              input: {
                readOnly,
              },
            }}
            disabled={!autoUpdateMiles || autoUpdateMiles.useCalculatedMilesPerMonth}
          />
        </Box>
      </RadioGroup>
    </FormControl>
  );
});

export type AutoUpdateMilesToggleProps = {
  className?: string;
};
export const AutoUpdateMilesToggle = React.memo(function AutoUpdateMilesToggle({
  className,
}: AutoUpdateMilesToggleProps) {
  const { classes } = useStyles();
  const {
    value: autoUpdateMiles,
    onValueChange: setAutoUpdateMiles,
    error,
  } = vehicleFormHooks.useField('autoUpdateMiles');
  const [isCustomizeDialogOpen, setIsCustomizeDialogOpen] = useState(false);
  const customizeStartAutoUpdateRef = useRef<typeof autoUpdateMiles | null>(null);

  const autoMilesDescriptionText = vehicleFormHooks.useSelector(selectAutoUpdateMilesDescriptionText);

  const kbbV2Enabled = useAppSelector(vehicleSelectors.getEnableKbbV2);

  const handleOpenCustomize = () => {
    customizeStartAutoUpdateRef.current = autoUpdateMiles;
    setIsCustomizeDialogOpen(true);
  };

  const handleCloseCustomize = () => {
    setIsCustomizeDialogOpen(false);
  };

  const handleCloseCustomizeAndRevertState = () => {
    setAutoUpdateMiles(customizeStartAutoUpdateRef.current);
    handleCloseCustomize();
  };

  if (!kbbV2Enabled) {
    return null;
  }

  return (
    <>
      <FormControl fullWidth className={className}>
        <FormControlLabel
          control={<Checkbox id='vehicle-auto-update-miles-toggle' size={'small'} />}
          onChange={(e, checked) => {
            checked
              ? setAutoUpdateMiles({
                  autoUpdateMiles: true,
                  useCalculatedMilesPerMonth: true,
                  customMilesPerMonth: null,
                })
              : setAutoUpdateMiles(null);
          }}
          checked={autoUpdateMiles?.autoUpdateMiles ?? false}
          label={'Automatically update miles'}
        />
        {autoUpdateMiles?.autoUpdateMiles && (
          <Box className={classes.autoUpdateToggleDetails}>
            <Typography variant='paragraphRegular'>{autoMilesDescriptionText}</Typography>
            <QButton
              variant='text'
              disableRipple
              className={classes.autoUpdateCustomizeButton}
              onClick={handleOpenCustomize}
            >
              Customize
            </QButton>
          </Box>
        )}
      </FormControl>

      <StdDialog
        title='Customize automatic miles update'
        onClose={handleCloseCustomizeAndRevertState}
        open={isCustomizeDialogOpen}
      >
        <StdDialogContent>
          <AutoUpdateMilesRadioGroup />
        </StdDialogContent>
        <StdDialogActions
          primary={{
            children: 'Save',
            onClick: () => {
              handleCloseCustomize();
            },
            variant: 'contained',
            disabled: !!error,
          }}
          secondary={{
            children: 'Cancel',
            onClick: () => {
              handleCloseCustomizeAndRevertState();
            },
          }}
        />
      </StdDialog>
    </>
  );
});
